import React, { useEffect, useState, useCallback } from 'react';
import config from '../config';

// Busca sesión in_progress en MySQL (prioridad para simulacros)
async function findMySQLInProgress() {
  try {
    const apiUrl = config.getApiUrl();
    const res = await fetch(`${apiUrl}/exam-sessions/in-progress`);
    const data = await res.json();
    if (data.found && data.session) {
      return {
        _source: 'mysql',
        sessionId: data.session.id,
        title: data.session.exam_title || 'Simulacro en progreso',
        currentQuestion: data.session.current_question || 0,
        totalQuestions: data.session.total_questions || 0,
        accuracy: data.session.accuracy || 0,
      };
    }
  } catch (_) {}
  return null;
}

// Busca el estado de simulacro pausado más reciente en localStorage
function findPausedSimulation() {
  try {
    const keys = Object.keys(localStorage).filter(k => k.startsWith('mediexampro_quiz_state_'));
    let latest = null;
    for (const key of keys) {
      const raw = localStorage.getItem(key);
      if (!raw) continue;
      try {
        const data = JSON.parse(raw);
        if (data && data.isPaused && !data.showResults && (data.timeLeft ?? 0) > 0) {
          if (!latest || (data.timestamp || 0) > (latest.timestamp || 0)) {
            latest = { ...data, storageKey: key };
          }
        }
      } catch (_) {}
    }
    return latest;
  } catch (e) {
    return null;
  }
}

const PausedSimulationBar = ({ darkMode, onResume }) => {
  const [paused, setPaused] = useState(null);

  const loadState = useCallback(async () => {
    // Prioridad 1: MySQL in_progress
    const mysqlSession = await findMySQLInProgress();
    if (mysqlSession) {
      setPaused(mysqlSession);
      return;
    }
    // Prioridad 2: localStorage pausado
    const found = findPausedSimulation();
    setPaused(found);
  }, []);

  useEffect(() => {
    loadState();
    const onEvent = () => loadState();
    const onVisibility = () => { if (!document.hidden) loadState(); };
    window.addEventListener('mediexam_quiz_state', onEvent);
    window.addEventListener('focus', onEvent);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('mediexam_quiz_state', onEvent);
      window.removeEventListener('focus', onEvent);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [loadState]);

  if (!paused) return null;

  const isMySQLResume = paused._source === 'mysql';
  const label = isMySQLResume
    ? `${paused.title} (${paused.currentQuestion}/${paused.totalQuestions})`
    : (paused.title || 'Simulacro en pausa');
  const minutes = isMySQLResume ? null : Math.floor((paused.timeLeft || 0) / 60);
  const seconds = isMySQLResume ? null : (paused.timeLeft || 0) % 60;

  return (
    <div className="fixed inset-x-0 bottom-4 z-40 px-4">
      <div className={`max-w-3xl mx-auto rounded-xl shadow-2xl border backdrop-blur ${
        darkMode
          ? 'bg-gray-900/70 border-cyan-500/30 text-gray-100'
          : 'bg-white/90 border-gray-200 text-gray-800'
      } flex items-center justify-between px-4 py-3`}
        style={{ pointerEvents: 'auto' }}
      >
        <div className="flex items-center gap-3">
          <div className={`w-2 h-2 rounded-full animate-pulse ${darkMode ? 'bg-yellow-400' : 'bg-yellow-500'}`} />
          <div className="text-sm">
            <div className="font-semibold">{label}</div>
            <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              {isMySQLResume
                ? `Accuracy: ${paused.accuracy}%`
                : `Tiempo restante: ${minutes}:${seconds.toString().padStart(2, '0')}`
              }
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => onResume?.(paused)}
            className={`${darkMode ? 'bg-green-600 hover:bg-green-500 text-white' : 'bg-green-500 hover:bg-green-600 text-white'} px-3 py-2 rounded-lg text-sm font-semibold transition-colors`}
          >
            Reanudar
          </button>
        </div>
      </div>
    </div>
  );
};

export default PausedSimulationBar;

